import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  MenuItem,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as ChangeStatusIcon,
  CloudUpload as ImportIcon,
  LocalShipping as ShipIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { 
  API_ENDPOINTS, 
  STATUS_OPTIONS, 
  SHIFT_OPTIONS,
} from '../../config';
import { PRODUCTS, GRADES, BRANDS, PACK_SIZES, PACK_STYLES } from '../../utils/DROPDOWN_OPTIONS';
import { loadNavIdsFromDatabase, loadDropdowns } from '../../utils/NAV_ID_MAPPING';
import ChangeStatusModal from '../modals/ChangeStatusModal';
import ImportDataModal from '../modals/ImportDataModal';
import MakeShipmentModal from '../modals/MakeShipmentModal';
import EditEntryModal from '../modals/EditEntryModal';
import AddNavIdModal from '../modals/AddNavIdModal';
import AddEntryModal from '../modals/AddEntryModal';
import ExportButton from '../../utils/ExportButton';

// Helper function to get auth config for axios requests
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// ✅ Date formatting function to convert "2025-11-01" to "Nov 01, 2025"
const formatDateToDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

// Format timestamp to IST (India Standard Time)
const formatTimestampIST = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  // Parse the timestamp and convert to IST
  const date = new Date(timestamp);
  
  // Format date
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  // Format time in 12-hour format
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  
  return `${month} ${day}, ${year} ${hours}:${minutes} ${ampm}`;
};

// ✅ Function to format GRADE values - handles Excel date conversion and removes leading zeros
const formatGrade = (grade) => {
  if (!grade) return '';
  
  const gradeStr = String(grade).trim();
  
  // Case 1: Excel converted to date format like "6-Aug", "8-Dec"
  // Convert back to proper grade format: "6-Aug" → "6/8"
  const monthMap = {
    'jan': 1, 'feb': 2, 'mar': 3, 'apr': 4,
    'may': 5, 'jun': 6, 'jul': 7, 'aug': 8,
    'sep': 9, 'oct': 10, 'nov': 11, 'dec': 12
  };
  
  const dateMatch = gradeStr.match(/^(\d{1,2})-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/i);
  if (dateMatch) {
    const firstNumber = parseInt(dateMatch[1], 10);
    const monthAbbr = dateMatch[2].toLowerCase();
    const secondNumber = monthMap[monthAbbr];
    if (secondNumber) {
      return `${firstNumber}/${secondNumber}`;
    }
  }
  
  // Case 2: Handle grade patterns like "06/8", "21/25", "16/20"
  // Remove leading zeros from the first number
  const slashMatch = gradeStr.match(/^0*(\d+)\/(\d+)$/);
  if (slashMatch) {
    return `${slashMatch[1]}/${slashMatch[2]}`;
  }
  
  // Case 3: Handle hyphen format like "6-8" or "31-40" (but not date format)
  const hyphenMatch = gradeStr.match(/^(\d{1,3})-(\d{1,3})$/);
  if (hyphenMatch) {
    return `${hyphenMatch[1]}/${hyphenMatch[2]}`;
  }
  
  // If no match, return as is
  return gradeStr;
};

// ✅ Function to format PACK_SIZE values - converts X to /
const formatPackSize = (packSize) => {
  if (!packSize) return '';
  
  // Convert "X" to "/" in pack size
  // Examples: "3X7 LB" → "3/7 LB", "10X2 LB" → "10/2 LB"
  const formatted = String(packSize).trim().replace(/X/gi, '/');
  
  return formatted;
};

// ✅ FIXED: Calculate total KGs and Pounds from pack_size, cartons, loose_bags, loose_kgs
const calculateWeights = (item) => {
  const cartons = Number(item.cartons) || 0;
  const loose_bags = Number(item.loose_bags) || 0;
  const loose_kgs = Number(item.loose_kgs) || 0;
  const pack_size = String(item.pack_size || '').trim();
  
  // Default values
  let total_kgs = 0;
  let total_pounds = 0;
  
  // Parse pack size: format is "bagsPerCarton/weightPerBag UNIT"
  // Examples: "12/12 OZ", "10/2 LB", "6/5 LB", "10X2 LB"
  const packMatch = pack_size.match(/(\d+\.?\d*)\s*[Xx\/]\s*(\d+\.?\d*)\s*(OZ|LB|LBS|KG|KGS)?/i);
  
  if (packMatch) {
    const bagsPerCarton = parseFloat(packMatch[1]) || 0;
    const weightPerBag = parseFloat(packMatch[2]) || 0;
    const unit = (packMatch[3] || 'LB').toUpperCase();
    
    // Calculate total bags (from cartons + loose bags)
    const totalBags = (bagsPerCarton * cartons) + loose_bags;
    
    // Calculate total weight in original unit
    // CRITICAL: totalBags × weightPerBag gives total weight
    const totalWeightInUnit = totalBags * weightPerBag;
    
    // Convert to POUNDS first (base unit for conversion)
    if (unit.includes('OZ')) {
      // OZ to LB: divide by 16 (16 oz = 1 lb)
      total_pounds = totalWeightInUnit / 16;
    } else if (unit.includes('KG')) {
      // KG to LB: multiply by 2.20
      total_pounds = totalWeightInUnit * 2.20;
    } else {
      // Assume LB
      total_pounds = totalWeightInUnit;
    }
    
    // Convert POUNDS to KG: divide by 2.20
    total_kgs = total_pounds / 2.20;
    
    // Add loose_kgs (already in KG)
    total_kgs = total_kgs + loose_kgs;
    total_pounds = total_pounds + (loose_kgs * 2.20);
    
  } else {
    // If pack_size format not recognized, just use loose_kgs
    total_kgs = loose_kgs;
    total_pounds = loose_kgs * 2.20;
  }
  
  return {
    total_kgs: Math.round(total_kgs * 100) / 100,  // Round to 2 decimal places
    total_pounds: Math.round(total_pounds * 100) / 100
  };
};

function DailyInventory({ user }) {
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};

  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // ✅ UPDATED: Filters with date_from and date_to instead of single date
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    pallet_id: '',
    cs1: '',
    cs2: '',
    show_excess: 'all',
    status: '',
    product: '',
    grade: '',
    pack_size: '',
    brand: '',
    trace_id: '',
    day_code: '',
    loose_bags_only: false,  // ✅ NEW: Filter for entries with loose bags
    loose_kgs_only: false,   // ✅ NEW: Filter for entries with loose kgs
    shift: '',               // ✅ NEW: Filter for shift (DAY/NIGHT)
    today_only: false,       // ✅ NEW: Filter for today's entries only
  });
  const debouncedFilters = useDebounce(filters, 500);


  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [selectedEntryForEdit, setSelectedEntryForEdit] = useState(null);
  const [selectedEntryForStatus, setSelectedEntryForStatus] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAddNavModal, setShowAddNavModal] = useState(false);

  // ✅ Check if any modal is open (to pause auto-refresh)
  const isAnyModalOpen = showAddModal || showEditModal || showChangeStatusModal || 
                         showImportModal || showShipmentModal || showAddNavModal;

  // ✅ Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // ✅ Load NAV IDs and Dropdowns from database on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        await loadDropdowns();
        console.log('✅ Dropdowns loaded successfully');
      } catch (err) {
        console.error('❌ Failed to load dropdowns:', err);
      }
      
      try {
        await loadNavIdsFromDatabase();
        console.log('✅ NAV IDs loaded successfully on component mount');
      } catch (err) {
        console.error('❌ Failed to load NAV IDs:', err);
      }
      await fetchData();
    };
    
    initializeData();
  }, [debouncedFilters]);

  // ✅ AUTO-REFRESH: Refresh data every 20 seconds (when enabled AND no modal is open)
  useEffect(() => {
    // Don't set interval if auto-refresh is disabled OR any modal is open
    if (!autoRefresh || isAnyModalOpen) {
      if (isAnyModalOpen) {
        console.log('⏸️ Auto-refresh paused - modal is open');
      }
      return;
    }

    const autoRefreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing data...');
      fetchData();
      setLastRefresh(new Date());
    }, 120000); // 2 minutes (120 seconds)

    // Cleanup interval on component unmount or when autoRefresh/modal state changes
    return () => {
      clearInterval(autoRefreshInterval);
      console.log('🛑 Auto-refresh interval cleared');
    };
  }, [debouncedFilters, autoRefresh, isAnyModalOpen]); // Re-create interval when filters, autoRefresh, or modal state changes

  // ✅ UPDATED: fetchData with date_from and date_to parameters
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        view: 'daily_inventory',
        date_from: filters.date_from,
        date_to: filters.date_to,
        pallet_id: filters.pallet_id,
        trace_id: filters.trace_id,
        cs1: filters.cs1,
        cs2: filters.cs2,
      });

      const response = await axios.get(
        `${API_ENDPOINTS.INVENTORY}?${params}`,
        getAuthConfig()
      );

      if (response.data.success) {
        // ✅ FIXED: Always recalculate weights using the corrected formula
        const processedData = (response.data.data || []).map(item => {
          // Always recalculate to ensure correct values
          const weights = calculateWeights(item);
          
          return {
            ...item,
            total_kgs: weights.total_kgs,
            total_pounds: weights.total_pounds
          };
        });
        
        setData(processedData);
        setTotals(response.data.totals || {});
      } else {
        setError('Failed to load data');
      }
    } catch (err) {
      console.error('Failed to fetch daily inventory:', err);
      setError('Failed to load daily inventory');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle NAV ID addition success
  const handleNavIdAdded = async () => {
    try {
      // Reload NAV IDs from database
      await loadNavIdsFromDatabase();
      console.log('✅ NAV IDs reloaded after addition');
      
      // Refresh inventory data to show updated NAV IDs
      await fetchData();
    } catch (err) {
      console.error('❌ Error in handleNavIdAdded:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  // ✅ UPDATED: handleClearFilters with date_from and date_to
  const handleClearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      pallet_id: '',
      cs1: '',
      cs2: '',
      show_excess: 'all',
      status: '',
      product: '',
      grade: '',
      pack_size: '',
      brand: '',
      trace_id: '',
      day_code: '',
      loose_bags_only: false,
      loose_kgs_only: false,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_ENDPOINTS.INVENTORY}?id=${id}`,
        getAuthConfig()
      );

      if (response.data.success) {
        alert('Entry deleted successfully!');
        fetchData();
      }
    } catch (err) {
      console.error('Failed to delete entry:', err);
      alert('Failed to delete entry: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  // ✅ FIXED: Using API_ENDPOINTS.BULK_DELETE instead of hardcoded localhost URL
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one row to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} selected record(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API_ENDPOINTS.BULK_DELETE,
        { ids: selectedRows },
        { 
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true 
        }
      );

      if (response.data.success) {
        alert(`Successfully deleted ${response.data.deleted_count} record(s)`);
        setSelectedRows([]);
        fetchData();
      } else {
        alert('Failed to delete records: ' + response.data.message);
      }
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      alert('Error deleting records: ' + (err.response?.data?.message || 'Unknown error'));
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map(row => row.id));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PRODUCED': 'success',
      'REPACK-IN': 'info',
      'RE-PRINTING': 'warning',
      'RE-PROCESS': 'secondary',
      'FCL EXCESS': 'error',
      'REPACK-OUT': 'warning',
      'SHIPPED': 'default',
      'EXCESS-RETURNED': 'warning',
    };
    return colors[status] || 'default';
  };

  // ✅ Filter logic for excess/normal entries and status
  const getFilteredData = () => {
    let filtered = data;
    
    // Filter by show_excess
    if (filters.show_excess === 'excess') {
      filtered = filtered.filter(item => 
        item.is_excess_return === 1 || 
        item.status === 'EXCESS-RETURNED' ||
        (item.remarks && item.remarks.toLowerCase().includes('excess'))
      );
    } else if (filters.show_excess === 'normal') {
      filtered = filtered.filter(item => 
        (!item.is_excess_return || item.is_excess_return === 0) &&
        item.status !== 'EXCESS-RETURNED' &&
        (!item.remarks || !item.remarks.toLowerCase().includes('excess'))
      );
    }
    
    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }
    
    // Filter by product
    if (filters.product) {
      filtered = filtered.filter(item => 
        item.product && item.product.toLowerCase().includes(filters.product.toLowerCase())
      );
    }
    
    // Filter by grade
    if (filters.grade) {
      filtered = filtered.filter(item => 
        item.grade && item.grade.toLowerCase().includes(filters.grade.toLowerCase())
      );
    }
    
    // Filter by pack_size
    if (filters.pack_size) {
      filtered = filtered.filter(item => 
        item.pack_size && item.pack_size.toLowerCase().includes(filters.pack_size.toLowerCase())
      );
    }
    
    // Filter by brand
    if (filters.brand) {
      filtered = filtered.filter(item => 
        item.brand && item.brand.toLowerCase().includes(filters.brand.toLowerCase())
      );
    }
    
    // Filter by trace_id
    if (filters.trace_id) {
      filtered = filtered.filter(item => 
        item.trace_id && item.trace_id.toLowerCase().includes(filters.trace_id.toLowerCase())
      );
    }
    
    // ✅ Filter by pallet_id (client-side)
    if (filters.pallet_id) {
      filtered = filtered.filter(item => 
        item.pallet_id && item.pallet_id.toLowerCase().includes(filters.pallet_id.toLowerCase())
      );
    }
    
    // Filter by day_code
    if (filters.day_code) {
      filtered = filtered.filter(item => 
        item.day_code && item.day_code.toLowerCase().includes(filters.day_code.toLowerCase())
      );
    }
    
    // ✅ NEW: Filter for entries with loose bags only
    if (filters.loose_bags_only) {
      filtered = filtered.filter(item => (Number(item.loose_bags) || 0) > 0);
    }
    
    // ✅ NEW: Filter for entries with loose kgs only
    if (filters.loose_kgs_only) {
      filtered = filtered.filter(item => (Number(item.loose_kgs) || 0) > 0);
    }
    
    // ✅ NEW: Filter by shift (DAY/NIGHT)
    if (filters.shift) {
      filtered = filtered.filter(item => 
        item.shift && item.shift.toUpperCase() === filters.shift.toUpperCase()
      );
    }
    
    // ✅ NEW: Filter for today's entries only
    if (filters.today_only) {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(item => {
        const itemDate = item.production_date ? item.production_date.split('T')[0] : '';
        return itemDate === today;
      });
    }
    
    return filtered;
  };

  // Calculate totals from filtered data
  const calculateFilteredTotals = (filteredData) => {
    return {
      cartons: filteredData.reduce((sum, item) => sum + (Number(item.cartons) || 0), 0),
      loose_bags: filteredData.reduce((sum, item) => sum + (Number(item.loose_bags) || 0), 0),
      loose_kgs: filteredData.reduce((sum, item) => sum + (Number(item.loose_kgs) || 0), 0),
      total_kgs: filteredData.reduce((sum, item) => sum + (Number(item.total_kgs) || 0), 0),
      total_pounds: filteredData.reduce((sum, item) => sum + (Number(item.total_pounds) || 0), 0),
    };
  };

  // ✅ NEW: Check if any filter is active (to show consolidated summary)
  const isAnyFilterActive = () => {
    return filters.status || filters.product || filters.grade || filters.pack_size || 
           filters.brand || filters.trace_id || filters.day_code || filters.pallet_id ||
           filters.loose_bags_only || filters.loose_kgs_only ||
           filters.show_excess !== 'all';
  };

  // ✅ NEW: Calculate consolidated summary grouped by Product, Grade, Pack Size, Brand
  const getConsolidatedSummary = (data) => {
    const summaryMap = {};
    
    data.forEach(item => {
      const key = `${item.product || 'N/A'}|${item.grade || 'N/A'}|${item.pack_size || 'N/A'}|${item.brand || 'N/A'}`;
      
      if (!summaryMap[key]) {
        summaryMap[key] = {
          product: item.product || 'N/A',
          grade: item.grade || 'N/A',
          pack_size: item.pack_size || 'N/A',
          brand: item.brand || 'N/A',
          entries: 0,
          cartons: 0,
          loose_bags: 0,
          loose_kgs: 0,
          total_kgs: 0,
          total_pounds: 0,
        };
      }
      
      summaryMap[key].entries += 1;
      summaryMap[key].cartons += Number(item.cartons) || 0;
      summaryMap[key].loose_bags += Number(item.loose_bags) || 0;
      summaryMap[key].loose_kgs += Number(item.loose_kgs) || 0;
      summaryMap[key].total_kgs += Number(item.total_kgs) || 0;
      summaryMap[key].total_pounds += Number(item.total_pounds) || 0;
    });
    
    return Object.values(summaryMap).sort((a, b) => b.cartons - a.cartons);
  };

  const excessReturnsCount = data.filter(item => 
    item.is_excess_return === 1 || 
    item.status === 'EXCESS-RETURNED' ||
    (item.remarks && item.remarks.toLowerCase().includes('excess'))
  ).length;

  const filteredData = getFilteredData();
  const filteredTotals = calculateFilteredTotals(filteredData);
  const consolidatedSummary = getConsolidatedSummary(filteredData);

  // ✅ NEW: Function to get shift styling (DAY = bold blue, NIGHT = bold purple)
  const getShiftStyle = (shift) => {
    if (shift === 'DAY') {
      return {
        fontWeight: 700,
        color: '#1565c0',
        backgroundColor: '#e3f2fd',
        padding: '4px 8px',
        borderRadius: '4px',
        display: 'inline-block',
        fontSize: '0.95rem',
      };
    } else if (shift === 'NIGHT') {
      return {
        fontWeight: 700,
        color: '#7b1fa2',
        backgroundColor: '#f3e5f5',
        padding: '4px 8px',
        borderRadius: '4px',
        display: 'inline-block',
        fontSize: '0.95rem',
      };
    }
    return {
      fontSize: '0.95rem',
    };
  };


  // ─── COLDSTORE THEME TOKENS ────────────────────────────────────────────────
  const CS = {
    grad: 'linear-gradient(135deg,#d32f2f 0%,#1565c0 100%)',
    primary: '#1565c0', pl: '#e3f2fd',
    success: '#2e7d32', sl: '#e8f5e9',
    warning: '#e65100', wl: '#fff3e0',
    danger: '#c62828', dl: '#ffebee',
    purple: '#6a1b9a',
    amber: '#f9a825', al: '#fffde7',
    teal: '#00695c',
    text: '#1a2332', muted: '#6b7a8f', light: '#9aa5b4',
    bg: '#f0f2f5', card: '#fff', border: '#e8ecf0', bl2: '#eef0f3',
    shadow: '0 2px 12px rgba(0,0,0,.09)', ssm: '0 1px 4px rgba(0,0,0,.06)',
  };

  // ─── REUSABLE SMALL HELPERS ────────────────────────────────────────────────
  // Coldstore-style stat card (for Day Summary)
  const StatCard = ({ label, value, unit, entries, color, bg, border: bc, active, onClick }) => (
    <Box onClick={onClick} sx={{
      borderRadius: '10px', p: '14px 10px 12px', border: `2px solid ${bc}`,
      bgcolor: bg, cursor: 'pointer', textAlign: 'center', transition: 'all .18s',
      outline: active ? `2px solid ${color}` : 'none', outlineOffset: '1px',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: CS.shadow },
    }}>
      <Box sx={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color, mb: 0.75, opacity: .85 }}>
        {label}{active && ' ✓'}
      </Box>
      <Box sx={{ fontSize: 28, fontWeight: 900, lineHeight: 1, color, mb: 0.25 }}>{value}</Box>
      <Box sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color, opacity: .7 }}>{unit}</Box>
      {entries !== undefined && <Box sx={{ fontSize: 10, color: CS.muted, mt: 0.5 }}>{entries} entries</Box>}
    </Box>
  );

  // Yesterday stat card (muted style)
  const StatCardMuted = ({ label, value, unit, entries, accentColor, active, onClick }) => (
    <Box onClick={onClick} sx={{
      borderRadius: '10px', p: '14px 10px 12px', border: `2px solid ${CS.border}`,
      bgcolor: '#f8f9fb', cursor: 'pointer', textAlign: 'center', transition: 'all .18s',
      outline: active ? `2px solid ${accentColor}` : 'none', outlineOffset: '1px',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: CS.shadow },
    }}>
      <Box sx={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: CS.muted, mb: 0.75 }}>
        {label}{active && ' ✓'}
      </Box>
      <Box sx={{ fontSize: 28, fontWeight: 900, lineHeight: 1, color: accentColor, mb: 0.25 }}>{value}</Box>
      <Box sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: CS.muted, opacity: .7 }}>{unit}</Box>
      {entries !== undefined && <Box sx={{ fontSize: 10, color: CS.light, mt: 0.5 }}>{entries} entries</Box>}
    </Box>
  );

  // Section divider label
  const SectionLabel = ({ icon, label, color = CS.primary, bg = CS.pl }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, mt: 2 }}>
      <Box sx={{ bgcolor: bg, color, px: 1.5, py: 0.3, borderRadius: '20px', fontSize: 10, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
        {icon} {label}
      </Box>
      <Box sx={{ flex: 1, height: '1px', bgcolor: CS.border }} />
    </Box>
  );

  // Card wrapper (white card with coldstore style)
  const CSCard = ({ children, sx = {} }) => (
    <Box sx={{ background: CS.card, border: `1px solid ${CS.border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: CS.ssm, ...sx }}>
      {children}
    </Box>
  );

  // Card header bar
  const CSCardHead = ({ left, right }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.25, bgcolor: '#fafbfc', borderBottom: `1px solid ${CS.border}` }}>
      {left}
      {right && <Box sx={{ fontSize: 11, color: CS.muted, fontWeight: 500 }}>{right}</Box>}
    </Box>
  );

  // ─── COMPUTE SUMMARY DATA ──────────────────────────────────────────────────
  const today    = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  const todayData     = data.filter(i => (i.production_date||'').split('T')[0] === todayStr);
  const yesterdayData = data.filter(i => (i.production_date||'').split('T')[0] === yesterdayStr);

  // Shift breakdowns
  const todayDay   = todayData.filter(i => i.shift?.toUpperCase() === 'DAY');
  const todayNight = todayData.filter(i => i.shift?.toUpperCase() === 'NIGHT');
  const yDay       = yesterdayData.filter(i => i.shift?.toUpperCase() === 'DAY');
  const yNight     = yesterdayData.filter(i => i.shift?.toUpperCase() === 'NIGHT');
  const sumC  = arr => arr.reduce((s,i) => s + (parseInt(i.cartons)||0), 0);
  const sumLb = arr => arr.reduce((s,i) => s + (parseFloat(i.total_pounds)||0), 0);

  // Status breakdowns – today
  const todayProduced   = todayData.filter(i => i.status === 'PRODUCED');
  const todayRepackIn   = todayData.filter(i => i.status === 'REPACK-IN');
  const todayReprinting = todayData.filter(i => i.status === 'RE-PRINTING');
  const todayCartons    = sumC(todayData);
  const todayLbs        = sumLb(todayData);

  // Status breakdowns – yesterday
  const yProduced   = yesterdayData.filter(i => i.status === 'PRODUCED');
  const yRepackIn   = yesterdayData.filter(i => i.status === 'REPACK-IN');
  const yReprinting = yesterdayData.filter(i => i.status === 'RE-PRINTING');
  const yCartons    = sumC(yesterdayData);
  const yLbs        = sumLb(yesterdayData);

  // Filter-active flags
  const isDayTodayF    = filters.shift === 'DAY'   && filters.today_only;
  const isNightTodayF  = filters.shift === 'NIGHT' && filters.today_only;
  const isDayYF        = filters.shift === 'DAY'   && filters.date_from === yesterdayStr;
  const isNightYF      = filters.shift === 'NIGHT' && filters.date_from === yesterdayStr;
  const isTodayF       = filters.today_only && !filters.shift;
  const isYF           = filters.date_from === yesterdayStr && !filters.shift;
  const isTodayProdF   = filters.status === 'PRODUCED'   && filters.today_only;
  const isTodayRpkF    = filters.status === 'REPACK-IN'  && filters.today_only;
  const isTodayRprF    = filters.status === 'RE-PRINTING'&& filters.today_only;
  const isYProdF       = filters.status === 'PRODUCED'   && filters.date_from === yesterdayStr;
  const isYRpkF        = filters.status === 'REPACK-IN'  && filters.date_from === yesterdayStr;
  const isYRprF        = filters.status === 'RE-PRINTING'&& filters.date_from === yesterdayStr;

  const fmtDay = d => d.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}).toUpperCase();

  return (
    <Box sx={{ background: CS.bg, minHeight: '100vh', p: 0 }}>

      {/* ══════════════ PAGE HEADER ══════════════ */}
      <Box sx={{
        background: CS.card, border: `1px solid ${CS.border}`, borderRadius: '10px',
        p: '12px 20px', mb: 2,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 2, boxShadow: CS.ssm,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '9px',
            background: CS.grad,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: 15, fontWeight: 800, color: CS.text, lineHeight: 1.2 }}>Daily Inventory</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: isAnyModalOpen ? '#ff9800' : (autoRefresh ? '#4caf50' : '#f44336'),
                animation: (autoRefresh && !isAnyModalOpen) ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } }
              }} />
              <Box sx={{ fontSize: 11, color: CS.muted }}>
                {isAnyModalOpen ? 'Paused (modal open)' : autoRefresh ? 'Live · Auto-refresh ON' : 'Auto-refresh OFF'}
                {' · '}{filteredData.length} entries ({data.length} total)
                {excessReturnsCount > 0 && ` · ${excessReturnsCount} Excess Returns`}
                {' · '}Last: {lastRefresh.toLocaleTimeString()}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}>
            <IconButton size="small" onClick={() => setAutoRefresh(!autoRefresh)}
              sx={{ bgcolor: autoRefresh ? CS.sl : CS.dl, border: `1px solid ${autoRefresh ? '#a5d6a7' : '#ef9a9a'}`, borderRadius: '6px', p: 0.75 }}>
              {autoRefresh ? <RefreshIcon sx={{ fontSize: 16, color: CS.success }} /> : <ClearIcon sx={{ fontSize: 16, color: CS.danger }} />}
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} size="small"
            onClick={() => { fetchData(); setLastRefresh(new Date()); }}
            sx={{ borderColor: CS.border, color: CS.muted, '&:hover': { borderColor: CS.primary, color: CS.primary, bgcolor: CS.pl } }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ══════════════ SECTION 1 · SHIFT-WISE SUMMARY ══════════════ */}
      <SectionLabel icon="🌅" label="Shift-Wise Summary" color="#f57f17" bg="#fffde7" />
      {filters.shift && (
        <Button size="small" color="warning" variant="outlined" sx={{ mb: 1, fontSize: 11 }}
          onClick={() => setFilters(prev => ({ ...prev, shift: '', today_only: false, date_from: '', date_to: '' }))}>
          Clear Shift Filter
        </Button>
      )}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {/* TODAY SHIFTS */}
        <Grid item xs={12} md={6}>
          <CSCard>
            <CSCardHead
              left={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.5px', color: CS.text }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', boxShadow: '0 0 0 3px rgba(76,175,80,.2)', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%,100%': {opacity:1}, '50%': {opacity:.3} } }} />
                TODAY
              </Box>}
              right={fmtDay(today)}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              {/* Today Day Shift */}
              <Box onClick={() => isDayTodayF ? setFilters(p=>({...p,shift:'',today_only:false})) : setFilters(p=>({...p,shift:'DAY',today_only:true,date_from:'',date_to:''}))}
                sx={{ p:'16px 18px', position:'relative', borderRight:`1px solid ${CS.border}`, cursor:'pointer', transition:'background .15s',
                  bgcolor: isDayTodayF ? CS.al : CS.card, outline: isDayTodayF ? `2px solid ${CS.amber}` : 'none', outlineOffset:'-2px',
                  '&:hover':{ bgcolor: CS.al } }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                  <Box sx={{ width:28,height:28,borderRadius:'6px',bgcolor:CS.al,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14 }}>☀️</Box>
                  <Box sx={{ fontSize:11,fontWeight:700,color:CS.muted,letterSpacing:'.3px' }}>Day Shift {isDayTodayF && '✓'}</Box>
                </Box>
                <Box sx={{ fontSize:40,fontWeight:900,lineHeight:1,letterSpacing:'-1px',mb:.5,color:CS.amber }}>{sumC(todayDay).toLocaleString()}</Box>
                <Box sx={{ fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.8px',color:CS.light }}>Cartons</Box>
                <Box sx={{ fontSize:10,color:CS.light,mt:.5 }}>{todayDay.length} entries · {sumLb(todayDay).toLocaleString(undefined,{maximumFractionDigits:0})} LBS</Box>
                <Box sx={{ position:'absolute',bottom:0,left:0,right:0,height:'3px',background:'linear-gradient(90deg,#f9a825,transparent)' }} />
              </Box>
              {/* Today Night Shift */}
              <Box onClick={() => isNightTodayF ? setFilters(p=>({...p,shift:'',today_only:false})) : setFilters(p=>({...p,shift:'NIGHT',today_only:true,date_from:'',date_to:''}))}
                sx={{ p:'16px 18px', position:'relative', cursor:'pointer', transition:'background .15s',
                  bgcolor: isNightTodayF ? CS.pl : CS.card, outline: isNightTodayF ? `2px solid ${CS.primary}` : 'none', outlineOffset:'-2px',
                  '&:hover':{ bgcolor: CS.pl } }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                  <Box sx={{ width:28,height:28,borderRadius:'6px',bgcolor:CS.pl,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14 }}>🌙</Box>
                  <Box sx={{ fontSize:11,fontWeight:700,color:CS.muted }}>Night Shift {isNightTodayF && '✓'}</Box>
                </Box>
                <Box sx={{ fontSize:40,fontWeight:900,lineHeight:1,letterSpacing:'-1px',mb:.5,color:CS.primary }}>{sumC(todayNight).toLocaleString()}</Box>
                <Box sx={{ fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.8px',color:CS.light }}>Cartons</Box>
                <Box sx={{ fontSize:10,color:CS.light,mt:.5 }}>{todayNight.length} entries · {sumLb(todayNight).toLocaleString(undefined,{maximumFractionDigits:0})} LBS</Box>
                <Box sx={{ position:'absolute',bottom:0,left:0,right:0,height:'3px',background:`linear-gradient(90deg,${CS.primary},transparent)` }} />
              </Box>
            </Box>
          </CSCard>
        </Grid>

        {/* YESTERDAY SHIFTS */}
        <Grid item xs={12} md={6}>
          <CSCard>
            <CSCardHead
              left={<Box sx={{ fontSize:12, fontWeight:800, textTransform:'uppercase', letterSpacing:'.5px', color:CS.muted }}>⏱ YESTERDAY</Box>}
              right={fmtDay(yesterday)}
            />
            <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr' }}>
              {/* Yesterday Day */}
              <Box onClick={() => isDayYF ? setFilters(p=>({...p,shift:'',today_only:false,date_from:'',date_to:''})) : setFilters(p=>({...p,shift:'DAY',today_only:false,date_from:yesterdayStr,date_to:yesterdayStr}))}
                sx={{ p:'16px 18px', position:'relative', borderRight:`1px solid ${CS.border}`, cursor:'pointer', transition:'background .15s',
                  bgcolor: isDayYF ? CS.al : CS.card, outline: isDayYF ? `2px solid ${CS.amber}` : 'none', outlineOffset:'-2px',
                  '&:hover':{ bgcolor: CS.al } }}>
                <Box sx={{ display:'flex',alignItems:'center',gap:1,mb:1 }}>
                  <Box sx={{ width:28,height:28,borderRadius:'6px',bgcolor:CS.al,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14 }}>☀️</Box>
                  <Box sx={{ fontSize:11,fontWeight:700,color:CS.muted }}>Day Shift {isDayYF && '✓'}</Box>
                </Box>
                <Box sx={{ fontSize:36,fontWeight:900,lineHeight:1,letterSpacing:'-1px',mb:.5,color:CS.light }}>{sumC(yDay).toLocaleString()}</Box>
                <Box sx={{ fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.8px',color:CS.light }}>Cartons</Box>
                <Box sx={{ fontSize:10,color:CS.light,mt:.5 }}>{yDay.length} entries · {sumLb(yDay).toLocaleString(undefined,{maximumFractionDigits:0})} LBS</Box>
                <Box sx={{ position:'absolute',bottom:0,left:0,right:0,height:'3px',background:'linear-gradient(90deg,#f9a825,transparent)' }} />
              </Box>
              {/* Yesterday Night */}
              <Box onClick={() => isNightYF ? setFilters(p=>({...p,shift:'',today_only:false,date_from:'',date_to:''})) : setFilters(p=>({...p,shift:'NIGHT',today_only:false,date_from:yesterdayStr,date_to:yesterdayStr}))}
                sx={{ p:'16px 18px', position:'relative', cursor:'pointer', transition:'background .15s',
                  bgcolor: isNightYF ? CS.pl : CS.card, outline: isNightYF ? `2px solid ${CS.primary}` : 'none', outlineOffset:'-2px',
                  '&:hover':{ bgcolor: CS.pl } }}>
                <Box sx={{ display:'flex',alignItems:'center',gap:1,mb:1 }}>
                  <Box sx={{ width:28,height:28,borderRadius:'6px',bgcolor:CS.pl,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14 }}>🌙</Box>
                  <Box sx={{ fontSize:11,fontWeight:700,color:CS.muted }}>Night Shift {isNightYF && '✓'}</Box>
                </Box>
                <Box sx={{ fontSize:36,fontWeight:900,lineHeight:1,letterSpacing:'-1px',mb:.5,color:CS.light }}>{sumC(yNight).toLocaleString()}</Box>
                <Box sx={{ fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.8px',color:CS.light }}>Cartons</Box>
                <Box sx={{ fontSize:10,color:CS.light,mt:.5 }}>{yNight.length} entries · {sumLb(yNight).toLocaleString(undefined,{maximumFractionDigits:0})} LBS</Box>
                <Box sx={{ position:'absolute',bottom:0,left:0,right:0,height:'3px',background:`linear-gradient(90deg,${CS.primary},transparent)` }} />
              </Box>
            </Box>
          </CSCard>
        </Grid>
      </Grid>

      {/* ══════════════ SECTION 2 · DAY SUMMARY ══════════════ */}
      <SectionLabel icon="📊" label="Day Summary" color={CS.success} bg={CS.sl} />
      {(filters.today_only || filters.date_from) && (
        <Button size="small" variant="outlined" sx={{ mb:1, fontSize:11 }}
          onClick={() => setFilters(p=>({...p,today_only:false,date_from:'',date_to:'',status:''}))}>
          Show All Data
        </Button>
      )}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {/* TODAY DAY SUMMARY */}
        <Grid item xs={12} md={6}>
          <CSCard>
            <CSCardHead
              left={<Box sx={{ display:'flex',alignItems:'center',gap:1 }}>
                <Box sx={{ bgcolor:CS.sl,color:CS.success,px:1.25,py:.25,borderRadius:'20px',fontSize:11,fontWeight:800,letterSpacing:'.5px',border:'1px solid #a5d6a7' }}>● Today</Box>
                <Box sx={{ fontSize:13,fontWeight:700,color:CS.text }}>📅 {fmtDay(today)}</Box>
              </Box>}
            />
            <Box sx={{ p:1.5 }}>
              <Box sx={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:1 }}>
                <StatCard label="Produced" value={sumC(todayProduced).toLocaleString()} unit="Cartons" entries={todayProduced.length}
                  color={CS.success} bg={CS.sl} border="#a5d6a7" active={isTodayProdF}
                  onClick={() => isTodayProdF ? setFilters(p=>({...p,today_only:false,status:''})) : setFilters(p=>({...p,today_only:true,shift:'',status:'PRODUCED',date_from:'',date_to:''}))} />
                <StatCard label="Repack-In" value={sumC(todayRepackIn).toLocaleString()} unit="Cartons" entries={todayRepackIn.length}
                  color={CS.primary} bg={CS.pl} border="#90caf9" active={isTodayRpkF}
                  onClick={() => isTodayRpkF ? setFilters(p=>({...p,today_only:false,status:''})) : setFilters(p=>({...p,today_only:true,shift:'',status:'REPACK-IN',date_from:'',date_to:''}))} />
                <StatCard label="Re-Print" value={sumC(todayReprinting).toLocaleString()} unit="Cartons" entries={todayReprinting.length}
                  color={CS.purple} bg="#f3e5f5" border="#ce93d8" active={isTodayRprF}
                  onClick={() => isTodayRprF ? setFilters(p=>({...p,today_only:false,status:''})) : setFilters(p=>({...p,today_only:true,shift:'',status:'RE-PRINTING',date_from:'',date_to:''}))} />
                <StatCard label="Total Cartons" value={todayCartons.toLocaleString()} unit="Cartons" entries={todayData.length}
                  color={CS.warning} bg={CS.wl} border="#ffcc80" active={isTodayF && !filters.status}
                  onClick={() => (isTodayF && !filters.status) ? setFilters(p=>({...p,today_only:false,status:''})) : setFilters(p=>({...p,today_only:true,shift:'',status:'',date_from:'',date_to:''}))} />
                <StatCard label="Total Weight" value={todayLbs.toLocaleString(undefined,{maximumFractionDigits:0})} unit="LBS"
                  color={CS.danger} bg={CS.dl} border="#f48fb1" active={isTodayF && !filters.status}
                  onClick={() => (isTodayF && !filters.status) ? setFilters(p=>({...p,today_only:false,status:''})) : setFilters(p=>({...p,today_only:true,shift:'',status:'',date_from:'',date_to:''}))} />
              </Box>
            </Box>
          </CSCard>
        </Grid>

        {/* YESTERDAY DAY SUMMARY */}
        <Grid item xs={12} md={6}>
          <CSCard>
            <CSCardHead
              left={<Box sx={{ display:'flex',alignItems:'center',gap:1 }}>
                <Box sx={{ bgcolor:CS.bl2,color:CS.muted,px:1.25,py:.25,borderRadius:'20px',fontSize:11,fontWeight:800,letterSpacing:'.5px',border:`1px solid ${CS.border}` }}>Yesterday</Box>
                <Box sx={{ fontSize:13,fontWeight:700,color:CS.text }}>📅 {fmtDay(yesterday)}</Box>
              </Box>}
            />
            <Box sx={{ p:1.5 }}>
              <Box sx={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:1 }}>
                <StatCardMuted label="Produced" value={sumC(yProduced).toLocaleString()} unit="Cartons" entries={yProduced.length}
                  accentColor={CS.primary} active={isYProdF}
                  onClick={() => isYProdF ? setFilters(p=>({...p,date_from:'',date_to:'',status:''})) : setFilters(p=>({...p,today_only:false,date_from:yesterdayStr,date_to:yesterdayStr,status:'PRODUCED',shift:''}))} />
                <StatCardMuted label="Repack-In" value={sumC(yRepackIn).toLocaleString()} unit="Cartons" entries={yRepackIn.length}
                  accentColor={CS.primary} active={isYRpkF}
                  onClick={() => isYRpkF ? setFilters(p=>({...p,date_from:'',date_to:'',status:''})) : setFilters(p=>({...p,today_only:false,date_from:yesterdayStr,date_to:yesterdayStr,status:'REPACK-IN',shift:''}))} />
                <StatCardMuted label="Re-Print" value={sumC(yReprinting).toLocaleString()} unit="Cartons" entries={yReprinting.length}
                  accentColor={CS.purple} active={isYRprF}
                  onClick={() => isYRprF ? setFilters(p=>({...p,date_from:'',date_to:'',status:''})) : setFilters(p=>({...p,today_only:false,date_from:yesterdayStr,date_to:yesterdayStr,status:'RE-PRINTING',shift:''}))} />
                <StatCardMuted label="Total Cartons" value={yCartons.toLocaleString()} unit="Cartons" entries={yesterdayData.length}
                  accentColor={CS.warning} active={isYF && !filters.status}
                  onClick={() => (isYF && !filters.status) ? setFilters(p=>({...p,date_from:'',date_to:'',status:''})) : setFilters(p=>({...p,today_only:false,date_from:yesterdayStr,date_to:yesterdayStr,status:'',shift:''}))} />
                <StatCardMuted label="Total Weight" value={yLbs.toLocaleString(undefined,{maximumFractionDigits:0})} unit="LBS"
                  accentColor={CS.danger} active={isYF && !filters.status}
                  onClick={() => (isYF && !filters.status) ? setFilters(p=>({...p,date_from:'',date_to:'',status:''})) : setFilters(p=>({...p,today_only:false,date_from:yesterdayStr,date_to:yesterdayStr,status:'',shift:''}))} />
              </Box>
            </Box>
          </CSCard>
        </Grid>
      </Grid>

      {/* ══════════════ SECTION 3 · OVERALL TOTALS ══════════════ */}
      <SectionLabel icon="📦" label="Overall Totals" color={CS.teal} bg="#e0f2f1" />
      {(filters.loose_bags_only || filters.loose_kgs_only) && (
        <Button size="small" variant="outlined" sx={{ mb:1, fontSize:11 }}
          onClick={() => setFilters(p=>({...p,loose_bags_only:false,loose_kgs_only:false}))}>
          Clear Loose Filter
        </Button>
      )}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label:'Total Cartons', value: Number(filteredTotals.cartons||0).toLocaleString(), color:CS.primary, borderLeft:`4px solid ${CS.primary}`, bg:CS.card, clickable:false },
          { label:'Total Loose Bags', value: Number(filteredTotals.loose_bags||0).toLocaleString(), sub:'Click to filter', color:'#0288d1', borderLeft:'4px solid #0288d1', bg: filters.loose_bags_only ? '#e1f5fe' : CS.card, clickable:true,
            onClick:() => setFilters(p=>({...p,loose_bags_only:!p.loose_bags_only,loose_kgs_only:false})), active: filters.loose_bags_only },
          { label:'Total Loose KGs', value: Number(filteredTotals.loose_kgs||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}), sub:'Click to filter', color:CS.warning, borderLeft:`4px solid ${CS.warning}`, bg: filters.loose_kgs_only ? CS.wl : CS.card, clickable:true,
            onClick:() => setFilters(p=>({...p,loose_kgs_only:!p.loose_kgs_only,loose_bags_only:false})), active: filters.loose_kgs_only },
          { label:'Total KGs', value: Number(filteredTotals.total_kgs||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}), color:CS.success, borderLeft:`4px solid ${CS.success}`, bg:CS.card, clickable:false },
          { label:'Total Pounds', value: Number(filteredTotals.total_pounds||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}), color:CS.warning, borderLeft:`4px solid ${CS.warning}`, bg:CS.card, clickable:false },
        ].map((item, idx) => (
          <Grid item xs={6} sm={4} md key={idx}>
            <Box onClick={item.onClick} sx={{
              background:item.bg, border:`1px solid ${CS.border}`, borderLeft: item.borderLeft,
              borderRadius:'10px', p:'13px 16px', boxShadow:CS.ssm, cursor: item.clickable ? 'pointer' : 'default',
              transition:'all .18s', outline: item.active ? `2px solid ${item.color}` : 'none', outlineOffset:'1px',
              '&:hover': item.clickable ? { boxShadow:CS.shadow, transform:'translateY(-1px)' } : {},
            }}>
              <Box sx={{ fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'1px',color:CS.muted,mb:.5 }}>
                {item.label}{item.active && ' ✓'}
              </Box>
              <Box sx={{ fontSize:26,fontWeight:900,lineHeight:1,color:item.color,mb:.25 }}>{item.value}</Box>
              {item.sub && <Box sx={{ fontSize:10,color:CS.light }}>{item.sub}</Box>}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* ══════════════ ACTION BUTTONS ══════════════ */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddModal(true)}
            sx={{ background:CS.grad, boxShadow:'none', textTransform:'none', fontWeight:700,
              '&:hover':{ background:'linear-gradient(135deg,#b71c1c 0%,#0d47a1 100%)' } }}>
            Add Entry
          </Button>
          <Button variant="contained" startIcon={<ShipIcon />} onClick={() => setShowShipmentModal(true)}
            sx={{ bgcolor:'#6a1b9a', boxShadow:'none', textTransform:'none', fontWeight:700,
              '&:hover':{ bgcolor:'#4a148c' } }}>
            Make Shipment
          </Button>
          <Button variant="outlined" color="success" startIcon={<AddIcon />} onClick={() => setShowAddNavModal(true)}
            sx={{ textTransform:'none', fontWeight:700, borderColor:'#a5d6a7', '&:hover':{ bgcolor:CS.sl } }}>
            Add NAV ID
          </Button>
          <ExportButton exportType="daily_inventory" label="Export Data" filters={filters} />
          {user.role === 'admin' && (
            <>
              <Button variant="contained" color="error" onClick={handleBulkDelete}
                disabled={selectedRows.length === 0} sx={{ textTransform:'none', fontWeight:700, boxShadow:'none' }}>
                Delete Selected ({selectedRows.length})
              </Button>
              <Button variant="outlined" startIcon={<ImportIcon />} onClick={() => setShowImportModal(true)}
                sx={{ fontWeight:700 }}>
                Import Data
              </Button>
            </>
          )}
        </Box>

        {/* ── LEGEND ── */}
        <Box sx={{ background:CS.card, border:`1px solid ${CS.border}`, borderRadius:'10px', p:'12px 16px', mb:2, boxShadow:CS.ssm }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Box sx={{ fontSize:11,fontWeight:700,color:CS.muted,mb:.75,textTransform:'uppercase',letterSpacing:'.5px' }}>Status Colors</Box>
              <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
                {[['#4caf50','PRODUCED'],['#2196f3','REPACK-IN'],['#9c27b0','RE-PRINTING'],['#d32f2f','RE-PROCESS'],['#ff9800','FCL EXCESS']].map(([c,l])=>(
                  <Box key={l} sx={{ display:'flex',alignItems:'center',gap:.5 }}>
                    <Box sx={{ width:12,height:12,borderRadius:'3px',bgcolor:c }} />
                    <Box sx={{ fontSize:11,color:CS.muted }}>{l}</Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ fontSize:11,fontWeight:700,color:CS.muted,mb:.75,textTransform:'uppercase',letterSpacing:'.5px' }}>Shift</Box>
              <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
                <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>
                  <Box sx={{ px:'6px',py:'2px',borderRadius:'4px',bgcolor:CS.pl,color:CS.primary,fontWeight:700,fontSize:'0.7rem' }}>DAY</Box>
                  <Box sx={{ fontSize:11,color:CS.muted }}>Day Shift</Box>
                </Box>
                <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>
                  <Box sx={{ px:'6px',py:'2px',borderRadius:'4px',bgcolor:'#f3e5f5',color:CS.purple,fontWeight:700,fontSize:'0.7rem' }}>NIGHT</Box>
                  <Box sx={{ fontSize:11,color:CS.muted }}>Night Shift</Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ fontSize:11,fontWeight:700,color:CS.muted,mb:.75,textTransform:'uppercase',letterSpacing:'.5px' }}>Row Highlight</Box>
              <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
                <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>
                  <Box sx={{ width:12,height:12,bgcolor:'#fff3e0',border:'2px solid #ff9800',borderRadius:'3px' }} />
                  <Box sx={{ fontSize:11,color:CS.muted }}>Excess Return</Box>
                </Box>
                <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>
                  <Box sx={{ width:12,height:12,bgcolor:'white',border:'1px solid #ccc',borderRadius:'3px' }} />
                  <Box sx={{ fontSize:11,color:CS.muted }}>Normal</Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ── STATUS FILTER BAR ── */}
        <Box sx={{
          background: filters.status ? CS.pl : CS.card,
          border: `2px solid ${filters.status ? '#90caf9' : CS.border}`,
          borderRadius:'10px', p:'12px 16px', mb:2, boxShadow:CS.ssm,
        }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
            <Box sx={{ fontSize:13, fontWeight:700, color:CS.primary, whiteSpace:'nowrap' }}>📦 Filter by Status:</Box>
            <TextField select value={filters.status} onChange={e=>handleFilterChange('status',e.target.value)} size="small"
              sx={{ minWidth:200, '& .MuiOutlinedInput-root':{ fontWeight:700, fontSize:'1rem', bgcolor:'white',
                '& fieldset':{ borderWidth:2, borderColor: filters.status ? CS.primary : '#bdbdbd' },
                '&:hover fieldset':{ borderColor:CS.primary }, '&.Mui-focused fieldset':{ borderColor:CS.primary } } }}
              SelectProps={{ displayEmpty:true }}>
              <MenuItem value=""><em style={{color:'#666'}}>All Statuses</em></MenuItem>
              <MenuItem value="PRODUCED" sx={{fontWeight:600,color:'#4caf50'}}>📦 PRODUCED</MenuItem>
              <MenuItem value="REPACK-IN" sx={{fontWeight:600,color:'#2196f3'}}>↩ REPACK-IN</MenuItem>
              <MenuItem value="RE-PRINTING" sx={{fontWeight:600,color:'#9c27b0'}}>🖨 RE-PRINTING</MenuItem>
              <MenuItem value="RE-PROCESS" sx={{fontWeight:600,color:'#d32f2f'}}>🔄 RE-PROCESS</MenuItem>
              <MenuItem value="FCL EXCESS" sx={{fontWeight:600,color:'#ff9800'}}>⚠ FCL EXCESS</MenuItem>
            </TextField>
            {filters.status && (
              <>
                <Chip label={`Showing: ${filters.status}`} color="primary" sx={{fontWeight:700}} />
                <Button variant="outlined" color="error" size="small" startIcon={<ClearIcon />}
                  onClick={()=>handleFilterChange('status','')} sx={{fontWeight:600}}>
                  Clear
                </Button>
              </>
            )}
            <Box sx={{ display:'flex', gap:.75, flexWrap:'wrap', ml:'auto' }}>
              {[
                {status:'PRODUCED',color:'#4caf50',icon:'📦'},
                {status:'REPACK-IN',color:'#2196f3',icon:'↩'},
                {status:'RE-PRINTING',color:'#9c27b0',icon:'🖨'},
                {status:'RE-PROCESS',color:'#d32f2f',icon:'🔄'},
                {status:'FCL EXCESS',color:'#ff9800',icon:'⚠'},
              ].map(({status,color,icon})=>{
                const count = data.filter(i=>i.status===status).reduce((s,i)=>s+(parseInt(i.cartons)||0),0);
                const isActive = filters.status === status;
                return (
                  <Chip key={status} label={`${icon} ${count.toLocaleString()}`} size="small"
                    onClick={()=>handleFilterChange('status', isActive?'':status)}
                    sx={{ bgcolor: isActive?color:`${color}20`, color: isActive?'white':color,
                      fontWeight:700, cursor:'pointer', border:`2px solid ${color}`,
                      '&:hover':{ bgcolor: isActive?color:`${color}40` } }} />
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* ── FILTERS PANEL ── */}
        {user?.role?.toLowerCase() === 'admin' ? (
          <Box sx={{ background:CS.card,border:`1px solid ${CS.border}`,borderRadius:'10px',p:'12px 16px',mb:2,boxShadow:CS.ssm }}>
            <Box sx={{ fontSize:12,fontWeight:700,color:CS.muted,mb:1.5,textTransform:'uppercase',letterSpacing:'.5px' }}>Filters</Box>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={2}><TextField fullWidth type="date" label="From Date" value={filters.date_from} onChange={e=>handleFilterChange('date_from',e.target.value)} size="small" InputLabelProps={{shrink:true}} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="date" label="To Date" value={filters.date_to} onChange={e=>handleFilterChange('date_to',e.target.value)} size="small" InputLabelProps={{shrink:true}} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Pallet ID" value={filters.pallet_id} onChange={e=>handleFilterChange('pallet_id',e.target.value)} size="small" placeholder="Type to filter..." InputProps={{ endAdornment: filters.pallet_id && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange('pallet_id','')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="CS1 (Bin Location)" value={filters.cs1} onChange={e=>handleFilterChange('cs1',e.target.value)} size="small" placeholder="Type to filter..." InputProps={{ endAdornment: filters.cs1 && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange('cs1','')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="CS2 (Bin Location)" value={filters.cs2} onChange={e=>handleFilterChange('cs2',e.target.value)} size="small" placeholder="Type to filter..." InputProps={{ endAdornment: filters.cs2 && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange('cs2','')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={2}><TextField select fullWidth label="Show Entries" value={filters.show_excess} onChange={e=>handleFilterChange('show_excess',e.target.value)} size="small"><MenuItem value="all">All Entries</MenuItem><MenuItem value="excess">Excess Returns Only</MenuItem><MenuItem value="normal">Normal Entries Only</MenuItem></TextField></Grid>
              <Grid item xs={12} md={3}><Autocomplete options={PRODUCTS} value={filters.product||null} onChange={(e,v)=>handleFilterChange('product',v||'')} renderInput={p=><TextField {...p} label="Product" size="small" placeholder="Type to search..."/>} freeSolo autoHighlight clearOnEscape /></Grid>
              <Grid item xs={12} md={3}><Autocomplete options={GRADES} value={filters.grade||null} onChange={(e,v)=>handleFilterChange('grade',v||'')} renderInput={p=><TextField {...p} label="Grade" size="small" placeholder="Type to search..."/>} freeSolo autoHighlight clearOnEscape /></Grid>
              <Grid item xs={12} md={3}><Autocomplete options={PACK_SIZES} value={filters.pack_size||null} onChange={(e,v)=>handleFilterChange('pack_size',v||'')} renderInput={p=><TextField {...p} label="Pack Size" size="small" placeholder="Type to search..."/>} freeSolo autoHighlight clearOnEscape /></Grid>
              <Grid item xs={12} md={3}><Autocomplete options={BRANDS} value={filters.brand||null} onChange={(e,v)=>handleFilterChange('brand',v||'')} renderInput={p=><TextField {...p} label="Brand" size="small" placeholder="Type to search..."/>} freeSolo autoHighlight clearOnEscape /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Trace ID" value={filters.trace_id} onChange={e=>handleFilterChange('trace_id',e.target.value)} size="small" InputProps={{ endAdornment: filters.trace_id && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange('trace_id','')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Day Code" value={filters.day_code} onChange={e=>handleFilterChange('day_code',e.target.value)} size="small" InputProps={{ endAdornment: filters.day_code && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange('day_code','')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} /></Grid>
              <Grid item xs={12} md={2}><Button variant="outlined" fullWidth onClick={handleClearFilters} sx={{height:'40px',fontWeight:700}}>Clear All Filters</Button></Grid>
            </Grid>
          </Box>
        ) : ['incharge','supervisor'].includes(user?.role?.toLowerCase().trim()) ? (
          <Box sx={{ background:CS.card,border:`1px solid ${CS.border}`,borderRadius:'10px',p:'12px 16px',mb:2,boxShadow:CS.ssm }}>
            <Box sx={{ fontSize:12,fontWeight:700,color:CS.muted,mb:1.5,textTransform:'uppercase',letterSpacing:'.5px' }}>Filters</Box>
            <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap', mb:1.5 }}>
              {['pallet_id','product','grade','pack_size'].map(field => (
                <TextField key={field} label={field.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                  value={filters[field]} onChange={e=>handleFilterChange(field,e.target.value)} size="small" placeholder="Type to filter..."
                  sx={{ flex:'1 1 180px', minWidth:180 }}
                  InputProps={{ endAdornment: filters[field] && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange(field,'')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} />
              ))}
            </Box>
            <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
              {['brand','trace_id','day_code'].map(field => (
                <TextField key={field} label={field.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                  value={filters[field]} onChange={e=>handleFilterChange(field,e.target.value)} size="small" placeholder="Type to filter..."
                  sx={{ flex:'1 1 180px', minWidth:180 }}
                  InputProps={{ endAdornment: filters[field] && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange(field,'')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} />
              ))}
              <Button variant="outlined" onClick={handleClearFilters} sx={{ flex:'1 1 180px', minWidth:180, height:'40px', fontWeight:700 }}>CLEAR</Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ background:CS.card,border:`1px solid ${CS.border}`,borderRadius:'10px',p:'12px 16px',mb:2,boxShadow:CS.ssm }}>
            <Box sx={{ fontSize:12,fontWeight:700,color:CS.muted,mb:1.5,textTransform:'uppercase',letterSpacing:'.5px' }}>Filters</Box>
            <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
              {['product','grade','pack_size','brand'].map(field => (
                <TextField key={field} label={field.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                  value={filters[field]} onChange={e=>handleFilterChange(field,e.target.value)} size="small" placeholder="Type to filter..."
                  sx={{ flex:'1 1 180px', minWidth:180 }}
                  InputProps={{ endAdornment: filters[field] && <InputAdornment position="end"><IconButton size="small" onClick={()=>handleFilterChange(field,'')}><ClearIcon fontSize="small"/></IconButton></InputAdornment> }} />
              ))}
              <Button variant="outlined" onClick={handleClearFilters} sx={{ flex:'1 1 180px', minWidth:180, height:'40px', fontWeight:700 }}>CLEAR</Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* ══════════════ CONSOLIDATED SUMMARY ══════════════ */}
      {isAnyFilterActive() && consolidatedSummary.length > 0 && (
        <Box sx={{ background:CS.card, border:`2px solid #a5d6a7`, borderRadius:'10px', p:2, mb:2, boxShadow:CS.ssm }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1.5 }}>
            <Box sx={{ fontSize:13, fontWeight:700, color:CS.success }}>📊 Consolidated Summary</Box>
            <Chip label={`${consolidatedSummary.length} groups`} size="small" color="success" />
            <Chip label={`${filteredData.length} entries`} size="small" variant="outlined" />
          </Box>
          <TableContainer sx={{ maxHeight:300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['PRODUCT','GRADE','PACK SIZE','BRAND','ENTRIES','CARTONS','LOOSE BAGS','LOOSE KGS','TOTAL KGS','TOTAL LBS'].map(h=>(
                    <TableCell key={h} align={['ENTRIES','CARTONS','LOOSE BAGS','LOOSE KGS','TOTAL KGS','TOTAL LBS'].includes(h)?'right':'left'}
                      sx={{ fontWeight:700, bgcolor:CS.success, color:'white', fontSize:11, py:1 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {consolidatedSummary.map((row,idx)=>(
                  <TableRow key={idx} sx={{ '&:nth-of-type(odd)':{ bgcolor:CS.sl } }}>
                    <TableCell sx={{fontWeight:600}}>{row.product}</TableCell>
                    <TableCell sx={{fontWeight:600}}>{formatGrade(row.grade)}</TableCell>
                    <TableCell>{formatPackSize(row.pack_size)}</TableCell>
                    <TableCell>{row.brand}</TableCell>
                    <TableCell align="right">{row.entries}</TableCell>
                    <TableCell align="right" sx={{fontWeight:700,color:CS.primary}}>{row.cartons.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.loose_bags.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.loose_kgs.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{fontWeight:600,color:CS.success}}>{row.total_kgs.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{fontWeight:600,color:CS.warning}}>{row.total_pounds.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor:'#c8e6c9' }}>
                  <TableCell colSpan={4} sx={{fontWeight:700}}>GRAND TOTAL</TableCell>
                  <TableCell align="right" sx={{fontWeight:700}}>{filteredData.length}</TableCell>
                  <TableCell align="right" sx={{fontWeight:700,color:CS.primary}}>{filteredTotals.cartons.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{fontWeight:700}}>{filteredTotals.loose_bags.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{fontWeight:700}}>{filteredTotals.loose_kgs.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{fontWeight:700,color:CS.success}}>{filteredTotals.total_kgs.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{fontWeight:700,color:CS.warning}}>{filteredTotals.total_pounds.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {isAnyFilterActive() && consolidatedSummary.length > 0 && (
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1.5 }}>
          <Box sx={{ fontSize:13, fontWeight:700, color:CS.primary }}>📋 Date-wise Details</Box>
          <Chip label={`${filteredData.length} entries`} size="small" variant="outlined" color="primary" />
        </Box>
      )}

      {/* ══════════════ MAIN TABLE ══════════════ */}
      {loading ? (
        <Box sx={{ display:'flex', justifyContent:'center', p:4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0}
          sx={{
            border:`2px solid ${CS.primary}`, borderRadius:'10px',
            minHeight:'400px', maxHeight:'calc(100vh - 180px)',
            width:'100%', overflow:'auto', position:'relative',
            '&::-webkit-scrollbar':{ width:'12px', height:'12px' },
            '&::-webkit-scrollbar-track':{ background:CS.bg, borderRadius:'6px' },
            '&::-webkit-scrollbar-thumb':{ background:CS.primary, borderRadius:'6px', border:`3px solid ${CS.bg}` },
            '&::-webkit-scrollbar-thumb:hover':{ background:'#0d47a1' },
            scrollbarWidth:'auto', scrollbarColor:`${CS.primary} ${CS.bg}`,
          }}>
          <Table stickyHeader size="small" sx={{
            width:'100%',
            '& .MuiTableCell-root':{ borderRight:`1px solid ${CS.bl2}`, padding:'8px 12px', fontSize:'0.95rem', lineHeight:1.4, whiteSpace:'nowrap', textAlign:'center' },
            '& .MuiTableCell-head':{ bgcolor:CS.primary, color:'white', fontWeight:700, fontSize:'0.85rem', textTransform:'uppercase', letterSpacing:'.5px', borderBottom:`3px solid #0d47a1`, position:'sticky', top:0, zIndex:1400, padding:'10px 12px', textAlign:'center' },
            '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)':{ bgcolor:'#fafbfc' },
            '& .MuiTableBody-root .MuiTableRow-root:hover':{ bgcolor:'#f0f7ff !important' },
          }}>
            <TableHead>
              <TableRow>
                {user.role === 'admin' && <TableCell sx={{width:50}}><input type="checkbox" checked={selectedRows.length===filteredData.length&&filteredData.length>0} onChange={handleSelectAll} style={{cursor:'pointer',transform:'scale(1.2)'}} /></TableCell>}
                {!['incharge','supervisor'].includes(user.role?.toLowerCase().trim()) && <TableCell sx={{width:100}}>Created By</TableCell>}
                {!['incharge','supervisor'].includes(user.role?.toLowerCase().trim()) && <TableCell sx={{width:150}}>Created At</TableCell>}
                <TableCell sx={{width:120}}>Status</TableCell>
                <TableCell sx={{width:110}}>Production Date</TableCell>
                <TableCell sx={{width:80}}>Shift</TableCell>
                <TableCell sx={{width:90}}>Product ID</TableCell>
                <TableCell sx={{width:100}}>NAV ID</TableCell>
                <TableCell sx={{width:90}}>Pack Style</TableCell>
                <TableCell sx={{width:90}}>Product</TableCell>
                <TableCell sx={{width:80}}>Grade</TableCell>
                <TableCell sx={{width:90}}>Pack Size</TableCell>
                <TableCell sx={{width:120}}>Brand</TableCell>
                <TableCell sx={{width:80}}>Cartons</TableCell>
                <TableCell sx={{width:90}}>Loose Bags</TableCell>
                <TableCell sx={{width:90}}>Loose KGs</TableCell>
                <TableCell sx={{width:100}}>Trace ID</TableCell>
                <TableCell sx={{width:100}}>Day Code</TableCell>
                <TableCell sx={{width:100}}>Lot No</TableCell>
                <TableCell sx={{width:90}}>Pallet ID</TableCell>
                <TableCell sx={{width:80}}>CS1</TableCell>
                <TableCell sx={{width:80}}>CS2</TableCell>
                <TableCell sx={{width:200,minWidth:180,maxWidth:220}}>Remarks</TableCell>
                <TableCell sx={{width:100,bgcolor:'#1a6db5 !important'}}>Total KGs</TableCell>
                <TableCell sx={{width:110,bgcolor:'#1a5fa0 !important'}}>Total Pounds</TableCell>
                <TableCell sx={{width:120}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user.role==='admin'?25:24} align="center">
                    <Box sx={{py:4,fontSize:'1rem',color:CS.muted}}>No data available</Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row) => (
                  <TableRow key={row.id} sx={{
                    bgcolor: (row.is_excess_return===1||row.status==='EXCESS-RETURNED') ? '#fff3e0' : row.status==='FCL EXCESS' ? '#ffebee' : 'inherit',
                    borderLeft: (row.is_excess_return===1||row.status==='EXCESS-RETURNED') ? '4px solid #ff9800' : row.status==='FCL EXCESS' ? '4px solid #f44336' : 'none',
                  }}>
                    {user.role==='admin' && <TableCell><input type="checkbox" checked={selectedRows.includes(row.id)} onChange={()=>handleSelectRow(row.id)} style={{cursor:'pointer',transform:'scale(1.2)'}} /></TableCell>}
                    {!['incharge','supervisor'].includes(user.role?.toLowerCase().trim()) && <TableCell><Typography variant="body2" sx={{fontSize:'0.95rem',fontWeight:700,color:'#000',textTransform:'uppercase'}}>{row.created_by_username||'User '+(row.created_by||'N/A')}</Typography></TableCell>}
                    {!['incharge','supervisor'].includes(user.role?.toLowerCase().trim()) && <TableCell><Typography variant="body2" sx={{fontSize:'0.9rem',color:'#666',fontWeight:700}}>{formatTimestampIST(row.created_at)}</Typography></TableCell>}
                    <TableCell>
                      <Chip label={row.status} color={getStatusColor(row.status)} size="small" sx={{fontWeight:600,fontSize:'0.85rem'}} />
                      {(row.is_excess_return===1||row.status==='EXCESS-RETURNED') && <Chip label="EXCESS" color="warning" size="small" sx={{ml:.5,fontWeight:'bold'}} />}
                    </TableCell>
                    <TableCell><Typography sx={{fontWeight:600,fontSize:'0.95rem'}}>{formatDateToDisplay(row.production_date)}</Typography></TableCell>
                    <TableCell><Box sx={getShiftStyle(row.shift)}>{row.shift||'-'}</Box></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.product_id||'-'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight:600, fontSize:'0.95rem', color: row.nav_id==='NOT AVAILABLE'?CS.danger:CS.success, bgcolor: row.nav_id==='NOT AVAILABLE'?CS.dl:CS.sl, px:1, py:.5, borderRadius:1, display:'inline-block' }}>
                        {row.nav_id||'-'}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.pack_style||'-'}</Typography></TableCell>
                    <TableCell><Typography sx={{fontWeight:600,fontSize:'0.95rem'}}>{row.product}</Typography></TableCell>
                    <TableCell><Typography sx={{fontWeight:600,fontSize:'0.95rem'}}>{formatGrade(row.grade)}</Typography></TableCell>
                    <TableCell><Typography sx={{fontWeight:600,fontSize:'0.95rem'}}>{formatPackSize(row.pack_size)}</Typography></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.brand}</Typography></TableCell>
                    <TableCell><Typography sx={{fontWeight:600,fontSize:'0.95rem'}}>{row.cartons}</Typography></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.loose_bags}</Typography></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.loose_kgs}</Typography></TableCell>
                    <TableCell><Typography sx={{fontWeight:600,fontSize:'0.95rem'}}>{row.trace_id}</Typography></TableCell>
                    <TableCell><Typography sx={{fontWeight:600,fontSize:'0.95rem'}}>{row.day_code}</Typography></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.lot_no}</Typography></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.pallet_id}</Typography></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.cs1}</Typography></TableCell>
                    <TableCell><Typography sx={{fontSize:'0.95rem'}}>{row.cs2}</Typography></TableCell>
                    <TableCell sx={{width:200,minWidth:180,maxWidth:220,whiteSpace:'normal',wordBreak:'break-word',textAlign:'left'}}>
                      <Tooltip title={row.remarks||''} arrow placement="top">
                        <Typography variant="body2" sx={{fontSize:'0.85rem',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {row.remarks||'-'}
                        </Typography>
                      </Tooltip>
                      {row.original_fcl_no && <Typography variant="caption" display="block" color="warning.main" sx={{mt:.5,fontWeight:'bold'}}>From FCL: {row.original_fcl_no} ({row.original_fcl_month})</Typography>}
                    </TableCell>
                    <TableCell sx={{bgcolor:'#e3f2fd',fontWeight:700,fontSize:'0.95rem'}}>{Number(row.total_kgs||0).toFixed(2)}</TableCell>
                    <TableCell sx={{bgcolor:'#fff3e0',fontWeight:700,fontSize:'0.95rem'}}>{Number(row.total_pounds||0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Box sx={{display:'flex',gap:.5,justifyContent:'center'}}>
                        <Tooltip title="Change Status"><IconButton size="small" color="primary" onClick={()=>{ setSelectedEntryForStatus(row); setShowChangeStatusModal(true); }}><ChangeStatusIcon fontSize="small"/></IconButton></Tooltip>
                        <Tooltip title="Edit Entry"><IconButton size="small" color="info" onClick={()=>{ setSelectedEntryForEdit(row); setShowEditModal(true); }}><EditIcon fontSize="small"/></IconButton></Tooltip>
                        {user.role==='admin' && <Tooltip title="Delete Entry"><IconButton size="small" color="error" onClick={()=>handleDelete(row.id)}><DeleteIcon fontSize="small"/></IconButton></Tooltip>}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ══════════════ MODALS ══════════════ */}
      <AddEntryModal open={showAddModal} onClose={()=>setShowAddModal(false)} onSuccess={fetchData} />
      <EditEntryModal open={showEditModal} onClose={()=>{ setShowEditModal(false); setSelectedEntryForEdit(null); }} onSuccess={fetchData} entryData={selectedEntryForEdit} user={user} />
      {user?.role==='admin' && <ImportDataModal open={showImportModal} onClose={()=>setShowImportModal(false)} onSuccess={fetchData} user={user} />}
      <MakeShipmentModal open={showShipmentModal} onClose={()=>setShowShipmentModal(false)} onSuccess={fetchData} />
      <ChangeStatusModal open={showChangeStatusModal} onClose={()=>{ setShowChangeStatusModal(false); setSelectedEntryForStatus(null); }} onSuccess={fetchData} entryData={selectedEntryForStatus} user={user} />
      <AddNavIdModal open={showAddNavModal} onClose={()=>setShowAddNavModal(false)} onSuccess={handleNavIdAdded} />
    </Box>
  );
}

export default DailyInventory;
